import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "trascription-control-wrapper" }

import { toRef, ref, computed, watch, onMounted } from 'vue';
import useAudioTranscription from '@/utilities/composables/useAudioTranscription';
import AudioPulse from '@/components/elements/AudioPulse.vue';
import SuggestionSpinner from '@/components/elements/SuggestionSpinner.vue';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import useTranslate from '@/utilities/composables/useTranslate';
import ButtonCustom from '@/components/elements/ButtonCustom';
import useIsElectronApp from '@/utilities/composables/useIsElectronApp';
import usePlatform from '@/utilities/composables/usePlatform';
import { useConfirmDialog } from '@vueuse/core';
import { Portal } from 'portal-vue';
import ConfirmationModal from '@/components/widgets/ConfirmationModal';
import constants from '@/utilities/constants';
import { useStore } from 'vuex';
import useUsageLimits from '@/utilities/composables/useUsageLimits';
import { useRoute } from 'vue-router';
import { getCurrentDateInSeconds } from '@/utilities/timeDifference';

const MAX_MINUTES_DIFF_FOR_AUTO_RECORDING = 5;


export default /*@__PURE__*/_defineComponent({
  __name: 'AudioTranscriptionControl',
  props: {
  resourceId: {
    type: String,
    required: true,
  },
  isQuickNote: {
    type: Boolean,
    required: false,
    default: false,
  },
},
  emits: ['openTranscripts', 'stopRecording', 'startRecording'],
  setup(__props, { emit: __emit }) {

const { iconPath, getCustomIconStyle } = useCustomIcon();
const { translate } = useTranslate();
const { isElectronApp } = useIsElectronApp();
const { isMac } = usePlatform();
const store = useStore();
const { routeToUpgradePlan } = useUsageLimits();
const route = useRoute();

const emit = __emit;

const audioPulseId = ref(0);

const changeTranscriptionNoteIdDialog = useConfirmDialog();

const props = __props;

const {
  startRecording,
  stopRecording,
  startRecordingForAnotherNote,
  alreadyRecordedForCurrentNote,
  isRecordingStarted,
  isRecordingStopped,
  isRecordingPending,
  hasTranscripts,
  transcriptionState,
  currentTranscriptionNoteId,
  currentTranscriptionNoteTitle,
  hasNoteTranscriptionFeatureFlag,
  isAutoStartTranscriptionEnabled,
  isAutoStartTranscriptionModeAlways,
  osSupportsTranscription,
} = useAudioTranscription(toRef(props, 'resourceId'));

const isStartedForCurrentNote = computed(() => {
  return transcriptionState.value.noteId === props.resourceId && isRecordingStarted.value;
});

const isPendingForCurrentNote = computed(() => {
  return transcriptionState.value.noteId === props.resourceId && isRecordingPending.value;
});

const changeCurrentTranscriptionNoteId = async () => {
  await startRecordingForAnotherNote(props.resourceId);
  emit('startRecording', transcriptionState.value.noteId);
  changeTranscriptionNoteIdDialog.cancel();
};

const isElectronOnMac = computed(() => isElectronApp.value && isMac.value);

const transcriptControlLabel = computed(() => {
  if (isStartedForCurrentNote.value) {
    return translate('pause');
  }

  return hasTranscripts.value ? translate('resume') : translate('transcribe');
});

const isRecordingStartedOnAnotherNote = computed(() => {
  return isRecordingStarted.value && currentTranscriptionNoteId.value !== props.resourceId;
});

const transcriptControlTooltip = computed(() => {
  if (!isElectronOnMac.value) return 'transcribingOnlyAvailableOnMac';
  if (!osSupportsTranscription.value) return 'transcribingOnlyAvailableOnNewerMacVersions';
  if (!hasNoteTranscriptionFeatureFlag.value) return 'transcriptionNotAvailableOnPlan';
  if (isRecordingStartedOnAnotherNote.value) return 'transcribeHereInsteadPositive';
  return !isRecordingStarted.value ? 'startTranscription' : 'stopTranscription';
});

const transcriptControlIcon = computed(() => {
  if (
    (!osSupportsTranscription.value || !hasNoteTranscriptionFeatureFlag.value) &&
    isElectronOnMac.value
  )
    return 'lock';

  return isRecordingStarted.value && isStartedForCurrentNote.value ? 'pause' : 'mic';
});

const handleOpenTranscripts = (): void => {
  emit('openTranscripts');
};

const isRecentNoteWithEvent = (noteId: string) => {
  const note = store.getters.getNotes[noteId] || {};
  if (!note.meeting_time || !note.fromCalendarEvent) return false;

  const currentTimestamp = getCurrentDateInSeconds();

  const timeDiffInMinutes = Math.abs(currentTimestamp - note.meeting_time) / 60;
  const isInEventTimeRange =
    note.meeting_metadata?.start_time <= currentTimestamp &&
    note.meeting_metadata?.end_time >= currentTimestamp;

  store.dispatch('localUpdateNoteProp', {
    noteId: note.id,
    propertyName: 'fromCalendarEvent',
    value: false,
  });

  return timeDiffInMinutes <= MAX_MINUTES_DIFF_FOR_AUTO_RECORDING || isInEventTimeRange;
};

const startRecordingOnNoteChange = (noteId: string, fromMeetingNotification: boolean = false) => {
  /**
   * Special case where transcription is always started from note from meeting notification.
   * Has priority over other conditions. A note can be created from a meeting notification from an event,
   * or as a new note.
   */
  if (fromMeetingNotification || store.getters.getNotes[noteId]?.fromMeetingNotification)
    startRecordingWrapper(true);
  if (!isElectronOnMac.value || !isAutoStartTranscriptionEnabled.value) return;

  if (
    (noteId === constants.newNote && isAutoStartTranscriptionModeAlways.value) ||
    isRecentNoteWithEvent(noteId)
  ) {
    startRecordingWrapper(true);
  }
};

watch(toRef(props, 'resourceId'), () => {
  if (!isRecordingStarted.value) audioPulseId.value += 1;
});

watch(
  () => props.resourceId,
  (newValue) => {
    startRecordingOnNoteChange(newValue, route?.query?.fromMeetingNotification);
  }
);

onMounted(() => {
  startRecordingOnNoteChange(props.resourceId);
});

const isControlLabelDisabled = computed((): boolean => {
  return !isElectronOnMac.value || isRecordingPending.value;
});

const isControlTooltipDisabled = computed((): boolean => {
  return isRecordingPending.value;
});

const startRecordingWrapper = async (attachCurrentDate?: boolean): void => {
  if (!isRecordingStopped.value) return;

  await startRecording(null, attachCurrentDate);
  emit('startRecording', transcriptionState.value.noteId);
};

const controlLabelClick = async (): void => {
  if (isControlLabelDisabled.value) {
    return;
  } else if (!hasNoteTranscriptionFeatureFlag.value) {
    routeToUpgradePlan();
  } else if (isRecordingStopped.value) {
    startRecordingWrapper();
  } else if (isStartedForCurrentNote.value) {
    stopRecording();
    emit('stopRecording');
  } else {
    changeTranscriptionNoteIdDialog.reveal();
  }
};

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (
        _unref(hasTranscripts) ||
        isStartedForCurrentNote.value ||
        isPendingForCurrentNote.value ||
        _unref(alreadyRecordedForCurrentNote)
      )
      ? _withDirectives((_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["transcript-button", {
        'pointer-events-none': _unref(isRecordingPending),
      }]),
          onClick: handleOpenTranscripts
        }, [
          (_unref(isRecordingPending))
            ? (_openBlock(), _createBlock(SuggestionSpinner, { key: 0 }))
            : _createCommentVNode("", true),
          _cache[0] || (_cache[0] = _createTextVNode()),
          (isStartedForCurrentNote.value)
            ? (_openBlock(), _createBlock(AudioPulse, {
                key: audioPulseId.value,
                class: "w-4 h-4",
                paused: !isStartedForCurrentNote.value
              }, null, 8, ["paused"]))
            : _createCommentVNode("", true),
          _cache[1] || (_cache[1] = _createTextVNode()),
          (!isStartedForCurrentNote.value)
            ? (_openBlock(), _createElementBlock("em", {
                key: 2,
                style: _normalizeStyle(_unref(getCustomIconStyle)(_unref(iconPath)('waveform'))),
                class: "custom-icons w-4 h-4"
              }, null, 4))
            : _createCommentVNode("", true),
          _cache[2] || (_cache[2] = _createTextVNode()),
          (!_unref(isRecordingPending))
            ? (_openBlock(), _createElementBlock("em", {
                key: 3,
                style: _normalizeStyle(_unref(getCustomIconStyle)(_unref(iconPath)('chevron-down'))),
                class: "custom-icons w-5 h-5"
              }, null, 4))
            : _createCommentVNode("", true)
        ], 2)), [
          [_directive_tooltip, {
        content: _unref(translate)('viewTranscript'),
        placement: 'top',
        offset: [0, 4],
        onShow: () => !_unref(isRecordingPending) && !_ctx.isMobile,
      }]
        ])
      : _createCommentVNode("", true),
    _cache[3] || (_cache[3] = _createTextVNode()),
    _withDirectives(_createVNode(_unref(ButtonCustom), {
      class: _normalizeClass({ 'disabled-button pointer-events-auto': isControlLabelDisabled.value }),
      text: transcriptControlLabel.value,
      type: 'secondary-regular secondary-button',
      "custom-icon-name": transcriptControlIcon.value,
      "custom-icon-color": '--var(iconDefault)',
      "custom-icon-size": '16px',
      "button-dimensions": { height: '24px' },
      onClick: controlLabelClick
    }, null, 8, ["class", "text", "custom-icon-name"]), [
      [_directive_tooltip, {
        content: _unref(translate)(transcriptControlTooltip.value),
        placement: 'top',
        onShow: () => !isControlTooltipDisabled.value && !_ctx.isMobile,
      }]
    ]),
    _cache[4] || (_cache[4] = _createTextVNode()),
    (_unref(changeTranscriptionNoteIdDialog).isRevealed.value)
      ? (_openBlock(), _createBlock(_unref(Portal), {
          key: 1,
          disabled: __props.isQuickNote,
          to: "root-widget"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(ConfirmationModal), {
              title: _unref(translate)('transcribeHereInstead'),
              description: 
          _unref(translate)('currentlyTranscribingAnotherNote', {
            noteTitle: _unref(currentTranscriptionNoteTitle),
          })
        ,
              "confirm-button-text": _unref(translate)('transcribeHere'),
              onConfirm: changeCurrentTranscriptionNoteId,
              onCancel: _unref(changeTranscriptionNoteIdDialog).cancel
            }, null, 8, ["title", "description", "confirm-button-text", "onCancel"])
          ]),
          _: 1
        }, 8, ["disabled"]))
      : _createCommentVNode("", true)
  ]))
}
}

})